import React, { useState, useEffect } from 'react';
import { Bell, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from '../../utils/toast';

interface Subscriber {
  id: string;
  display_name: string;
  email: string;
}

interface NotifySubscribersSectionProps {
  calendarId: string;
  calendarName: string;
}

export default function NotifySubscribersSection({ calendarId, calendarName }: NotifySubscribersSectionProps) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedSubscriber, setSelectedSubscriber] = useState<string>('');
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    setLoadingSubscribers(true);
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          user_id,
          profiles!subscriptions_user_id_fkey (
            id,
            display_name,
            email
          )
        `)
        .eq('calendar_id', calendarId)
        .eq('status', 'active');

      if (error) throw error;

      const formattedSubscribers = data
        .map(sub => sub.profiles)
        .filter(Boolean)
        .map(profile => ({
          id: profile.id,
          display_name: profile.display_name,
          email: profile.email
        }));

      setSubscribers(formattedSubscribers);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      toast.error('Failed to load subscribers');
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const handleSendNotification = async () => {
    if (!message.trim() || !selectedSubscriber) {
      toast.error('Please select a subscriber and enter a message');
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create notification for single subscriber
      const { error: notifyError } = await supabase
        .from('notifications')
        .insert({
          user_id: selectedSubscriber,
          type: 'calendar_message',
          data: {
            calendarId,
            calendarName,
            message: message.trim(),
            senderId: user.id
          }
        });

      if (notifyError) throw notifyError;

      // Send email notification
      const { error: emailError } = await supabase.functions.invoke('send-subscriber-notification', {
        body: {
          calendarId,
          calendarName,
          message: message.trim(),
          recipientType: 'single',
          subscriberId: selectedSubscriber,
          senderId: user.id
        }
      });

      if (emailError) throw emailError;

      toast.success('Notification sent successfully');
      setMessage('');
      setSelectedSubscriber('');
    } catch (err) {
      console.error('Error sending notification:', err);
      toast.error('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Notify Subscribers</h2>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Subscriber
          </label>
          <select
            value={selectedSubscriber}
            onChange={(e) => setSelectedSubscriber(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
            disabled={loadingSubscribers}
          >
            <option value="">Select a subscriber</option>
            {subscribers.map(subscriber => (
              <option key={subscriber.id} value={subscriber.id}>
                {subscriber.display_name} ({subscriber.email})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
            placeholder="Enter your message to the subscriber..."
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSendNotification}
            disabled={loading || !message.trim() || !selectedSubscriber}
            className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
          >
            <Bell className="h-4 w-4 mr-2" />
            {loading ? 'Sending...' : 'Send Notification'}
          </button>
        </div>
      </div>
    </div>
  );
}